import React from 'react';
import {
  SvgIconComponent,
} from '@mui/icons-material';
import clsx from 'clsx';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import Skeleton from 'core/components/Skeleton';
import {
  TempoCompletionMetricDTO,
  TempoSummaryDTO,
} from 'dtos';

type Props = {
  summary?: TempoSummaryDTO;
  isLoading?: boolean;
};

type SummaryItem = {
  id: string;
  label: string;
  metric?: TempoCompletionMetricDTO;
  Icon: SvgIconComponent;
  iconClassName: string;
};

type SummaryMetricCardProps = {
  item: SummaryItem;
};

function formatPercentage(value?: number | null): string {
  return `${(value ?? 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
}

function SummaryMetricCard({
  item,
}: SummaryMetricCardProps) {
  const { Icon } = item;

  return (
    <div className="flex min-h-[72px] min-w-0 flex-1 gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className={clsx(
        'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
        item.iconClassName,
      )}
      >
        <Icon className="!h-5 !w-5" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-xs font-medium text-gray-600">{item.label}</span>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="text-xl font-bold leading-none text-gray-900">
            {formatPercentage(item.metric?.completionPercentage)}
          </span>
        </div>
        <span className="mt-1 text-xs text-gray-500">
          {item.metric?.completedEncounters ?? 0}
          {' out of '}
          {item.metric?.totalEncounters ?? 0}
        </span>
      </div>
    </div>
  );
}

export default function SummaryCards({ summary, isLoading }: Props) {
  const items: SummaryItem[] = [{
    id: 'totalCompletion',
    label: 'Total Encounters vs Completed',
    metric: summary?.totalCompletion,
    Icon: CheckRoundedIcon,
    iconClassName: 'bg-blue-50 text-blue-600',
  }, {
    id: 'day1Completion',
    label: 'Day 1 Encounters vs Completed',
    metric: summary?.day1Completion,
    Icon: CheckRoundedIcon,
    iconClassName: 'bg-emerald-50 text-emerald-600',
  }, {
    id: 'day2Completion',
    label: 'Day 2 Encounters vs Completed',
    metric: summary?.day2Completion,
    Icon: CheckRoundedIcon,
    iconClassName: 'bg-violet-50 text-violet-600',
  }];

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      {isLoading ? items.map((item) => (
        <Skeleton
          key={item.id}
          variant="rounded"
          className="!h-[72px] !w-full"
        />
      )) : items.map((item) => (
        <SummaryMetricCard
          key={item.id}
          item={item}
        />
      ))}
    </div>
  );
}
